import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { UserPlus, Trash2, Send, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, PrimaryButton, Input, Select, EmptyState, Table, THead, TR, TH, TD, Badge } from "./_ui";

export default function InvitationsPanel() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ email: "", full_name: "", role: "member" });
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = () => api.get("/admin/invitations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!form.email || !form.full_name) return toast.error("Nom et email requis");
    setBusy(true);
    try {
      await api.post("/admin/invitations", form);
      toast.success(`Invitation envoyée à ${form.email}`);
      setForm({ email: "", full_name: "", role: "member" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette invitation ?")) return;
    await api.delete(`/admin/invitations/${id}`);
    toast.success("Supprimée"); await load();
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code); setTimeout(() => setCopied(null), 1500);
  };

  if (!items) return (<><PageHeader title="Invitations" /><Spinner /></>);

  return (
    <div data-testid="admin-invitations">
      <PageHeader
        title="Invitations"
        subtitle="Inviter de nouveaux membres ou administrateurs par email."
      />

      <Card className="p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input placeholder="Nom complet" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="inv-name" />
          <Input type="email" placeholder="email@exemple.cd" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inv-email" />
          <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="inv-role">
            <option value="member">Membre</option>
            <option value="admin">Administrateur</option>
          </Select>
          <PrimaryButton onClick={send} disabled={busy} className="justify-center" data-testid="inv-send">
            <Send size={14} /> Envoyer l'invitation
          </PrimaryButton>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Une invitation génère un code d'inscription unique. (Mode démo : aucun email n'est envoyé.)
        </p>
      </Card>

      {items.length === 0 ? (
        <EmptyState icon={UserPlus} title="Aucune invitation" hint="Envoyez la première invitation pour démarrer." />
      ) : (
        <Table>
          <THead><tr><TH>Nom</TH><TH>Email</TH><TH>Rôle</TH><TH>Code</TH><TH>Statut</TH><TH>Émise le</TH><TH /></tr></THead>
          <tbody>
            {items.map((i) => (
              <TR key={i.id}>
                <TD className="text-white">{i.full_name}</TD>
                <TD>{i.email}</TD>
                <TD><Badge color={i.role === "admin" ? "gold" : "blue"}>{i.role}</Badge></TD>
                <TD>
                  <button onClick={() => copy(i.code)} className="font-mono text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-[#D4AF37] inline-flex items-center gap-1.5">
                    {i.code}
                    {copied === i.code ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                  </button>
                </TD>
                <TD><Badge color={i.status === "pending" ? "slate" : "green"}>{i.status}</Badge></TD>
                <TD className="text-xs">{new Date(i.created_at).toLocaleDateString("fr-FR")}</TD>
                <TD>
                  <button onClick={() => remove(i.id)} className="text-slate-500 hover:text-[#CE1126]" data-testid={`inv-delete-${i.id}`}>
                    <Trash2 size={14} />
                  </button>
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
